import {
    faCheckCircle,
    faHourglass,
    faLock,
    faLockOpen,
    faTimesCircle, // faEquals,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';

import { useTheme } from '../../contexts/ThemeContext';

import styles from '../../../styles/partials/question-status.module.scss';

const propTypes = {
    question: AppPropTypes.question,
    answer: AppPropTypes.answer,
    ended: PropTypes.bool,
    withPill: PropTypes.bool,
    className: PropTypes.string,
};

const defaultProps = {
    question: null,
    answer: null,
    ended: false,
    withPill: false,
    className: null,
};

function QuestionStatus({ question, answer, ended, withPill, className }) {
    const { canAnswer = false, expired = false, evenAnswer = null } = question || {};
    const answered = answer !== null;
    const { valid = false, validated = false } = answer || {};

    const { name: theme = null, isVariant } = useTheme();
    const isMVP = theme === 'nhl' && isVariant('mvp');
    const isAllstar = theme === 'nhl' && isVariant('allstar');

    const { id, icon, color, backgroundColor, label } = useMemo(() => {
        const nhlColors = {
            rightColor: '#021846',
            rightBackgroundColor: '#D9FFAD',
            wrongColor: '#021846',
            wrongBackgroundColor: '#D63643',
            endedColor: '#021846',
            endedBackgroundColor: '#D63643',
            answeredColor: '#183164',
            answeredBackgroundColor: '#6CB0E2',
            availableColor: '#021846',
            availableBackgroundColor: '#FFF',
            expiredColor: '#D63643',
            expiredBackgroundColor: '#021846',
            lockedColor: '#FFF',
            lockedBackgroundColor: '#021846',
        };

        // TODO: make sure this is right mvp+allstar
        const mvpColors = {
            rightColor: '#021846',
            rightBackgroundColor: '#D9FFAD',
            wrongColor: '#CCC',
            wrongBackgroundColor: '#76232f',
            endedColor: '#CCC',
            endedBackgroundColor: '#76232f',
            answeredColor: '#183164',
            answeredBackgroundColor: '#77C5D5',
            availableColor: '#021846',
            availableBackgroundColor: '#FFF',
            expiredColor: '#FFF',
            expiredBackgroundColor: '#021846',
            lockedColor: '#FFF',
            lockedBackgroundColor: '#003057',
        };

        const allstarColors = {
            ...mvpColors,
            wrongBackgroundColor: '#C8102E',
            rightBackgroundColor: '#77C5D5',
            expiredColor: '#76232f',
            expiredBackgroundColor: '#FFF',
        };

        const nflColors = {
            rightColor: '#000F3C',
            rightBackgroundColor: '#269C40',
            wrongColor: '#000F3C',
            wrongBackgroundColor: '#AF292E',
            endedColor: '#000F3C',
            endedBackgroundColor: '#AF292E',
            answeredColor: '#000F3C',
            // answeredBackgroundColor: '#269C40',
            answeredBackgroundColor: '#bfc4d9',
            availableColor: '#000F3C',
            availableBackgroundColor: '#FFF',
            expiredColor: '#FFF',
            expiredBackgroundColor: '#000F3C',
            lockedColor: '#000F3C',
            lockedBackgroundColor: '##F3F',
        };

        let colors;

        if (theme === 'nfl') {
            colors = nflColors;
        }
        if (theme === 'nhl') {
            if (isMVP) {
                colors = mvpColors;
            }
            if (isAllstar) {
                colors = allstarColors;
            }
            if (!isMVP && !isAllstar) {
                colors = nhlColors;
            }
        }

        if (validated) {
            const validIcon = evenAnswer
                ? {
                      id: 'right',
                      icon: faCheckCircle, // faEquals, TODO: create an icon equals-circle
                      color: colors.rightColor,
                      backgroundColor: colors.rightBackgroundColor,
                      label: (
                          <FormattedMessage
                              defaultMessage="Even result"
                              description="Question status"
                          />
                      ),
                  }
                : {
                      id: 'right',
                      icon: faCheckCircle,
                      color: colors.rightColor,
                      backgroundColor: colors.rightBackgroundColor,
                      label: (
                          <FormattedMessage
                              defaultMessage="Good answer"
                              description="Question status"
                          />
                      ),
                  };
            return valid
                ? validIcon
                : {
                      id: 'wrong',
                      icon: faTimesCircle,
                      color: colors.wrongColor,
                      backgroundColor: colors.wrongBackgroundColor,
                      label: (
                          <FormattedMessage
                              defaultMessage="Wrong answer"
                              description="Question status"
                          />
                      ),
                  };
        }

        if (ended) {
            return {
                id: 'ended',
                icon: faHourglass,
                color: colors.endedColor,
                backgroundColor: colors.endedBackgroundColor,
                label: <FormattedMessage defaultMessage="Ended" description="Question status" />,
            };
        }

        if (answered) {
            return {
                id: 'answered',
                icon: faCheckCircle,
                color: colors.answeredColor,
                backgroundColor: colors.answeredBackgroundColor,
                label: <FormattedMessage defaultMessage="Answered" description="Question status" />,
            };
        }

        if (expired) {
            return {
                id: 'expired',
                icon: faHourglass,
                color: colors.expiredColor,
                backgroundColor: colors.expiredBackgroundColor,
                label: (
                    <FormattedMessage defaultMessage="Time expired" description="Question status" />
                ),
            };
        }

        if (canAnswer) {
            return {
                id: 'available',
                icon: faLockOpen,
                color: colors.availableColor,
                backgroundColor: colors.availableBackgroundColor,
                label: (
                    <FormattedMessage defaultMessage="Answer now" description="Question status" />
                ),
            };
        }

        return {
            id: 'locked',
            icon: faLock,
            color: colors.lockedColor,
            backgroundColor: colors.lockedBackgroundColor,
            label: <FormattedMessage defaultMessage="Locked" description="Question status" />,
        };
    }, [withPill, canAnswer, answered, validated, valid]);

    const invertColors = !withPill && id !== 'expired' && id !== 'locked';
    const withoutIcon = !withPill && (id === 'available' || id === 'locked');

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.withPill]: withPill,
                },
            ])}
            style={{
                color: invertColors ? backgroundColor : color,
                backgroundColor: withPill ? backgroundColor : null,
            }}
        >
            {!withoutIcon ? <FontAwesomeIcon className={styles.icon} icon={icon} /> : null}
            {withPill ? <span className={styles.label}>{label}</span> : null}
        </div>
    );
}

QuestionStatus.defaultProps = defaultProps;
QuestionStatus.propTypes = propTypes;

export default QuestionStatus;
